'use client';
import React, { useState, useEffect } from 'react';
import { IEvaluation } from '@/lib/evaluationsApi';
import * as Styled from './styled';
import { IDataFrameSchema } from '@/lib/dataFrameTypes';
import { TEvaluationModel, TModelDeveloper } from '@/lib/evaluationModelsApi';
import pluralize from 'pluralize';

import { Row, Stack, Card } from '@/ui/primatives';
import Input from '@/components/Input';
import Button from '@/ui/Button';
import * as Icon from '@/ui/Icon';
import { PromptEditorControl } from '../PromptEditor';
import TargetFieldSelect from '../TargetFieldSelect';
import { SourceFileInfo } from '../EvaluationFileInfo';
import SchemaRail from '../SchemaRail';

import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
// import { DevTool } from '@hookform/devtools';
import ModelSelect from '../ModelSelect';
import ProviderSelect from '../ProviderSelect';
import DeveloperSelect from '../DeveloperSelect';

import useAvailableModels from './useAvailableModels';

import { promptPlaceholder, extractValuesInCurlyBraces, getErrorMessages, getTypeOptions } from '../lib';

const formSchema = Yup.object().shape({
  name: Yup.string(),
  provider_id: Yup.string().required('Required'),
  developer_id: Yup.string().required('Required'),
  model_id: Yup.string().required('Required'),
  prompt: Yup.string().required('Required'),
  type: Yup.string().required('Required'),
  sample_count: Yup.number().required('Required'),
  target_column: Yup.string()
    .required('Required')
    .min(2, 'Minimum 2 characters')
    .max(50)
    .matches(/^[a-zA-Z0-9_]+$/, 'No Spaces or dashes'),
});

const SetupForm: React.FC<{
  evaluation: IEvaluation;
  availableModels: TEvaluationModel[];
  schema: IDataFrameSchema;
  children?: React.ReactNode;
  onExtractedFieldsChange?: (fields: string[]) => void;
  onNext: (data: IEvaluation) => void;
  onRunSample: (data: IEvaluation) => void;
}> = ({
  evaluation,
  availableModels,
  schema: initialSchema,
  children,
  onExtractedFieldsChange = () => {},
  onNext,
  onRunSample,
  ...rest
}) => {
  const [schema, setSchema] = useState<IDataFrameSchema>(initialSchema);
  const [prompt, setPrompt] = useState<string>(evaluation.prompt || '');
  const [targetColumn, setTargetColumn] = useState<string>(evaluation.target_column || 'prediction');
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const typeOptions = getTypeOptions(availableModels);

  const {
    providers,
    developers,
    models,
    inputType,
    outputType,
    setInputType,
    setOutputType,
    setProvider,
    setDeveloper,
  } = useAvailableModels({
    allModels: availableModels,
    initialInputType: evaluation.input_type || 'text',
    initialOutputType: evaluation.output_type || 'text',
    providerId: '',
    developerId: evaluation.model?.developer?.id || '',
  });

  // console.log('evaluation', evaluation);

  const extractedFields = extractValuesInCurlyBraces(prompt);
  useEffect(() => {
    onExtractedFieldsChange(extractedFields);
  }, [extractedFields]);

  const DEFAULT_MODEL_NAME = 'gpt-4o-mini';
  const defaultModel = availableModels.find((model) => model.name === DEFAULT_MODEL_NAME);
  const evalModel = availableModels.find((model) => model.id === evaluation.model?.id);

  const {
    handleSubmit,
    register,
    watch,
    reset,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      ...evaluation,
      target_column: evaluation.target_column,
      provider_id: evalModel?.provider?.id || defaultModel?.provider.id,
      developer_id: evalModel?.developer?.id || defaultModel?.developer?.id,
      model_id: evalModel?.id || defaultModel?.id,
      name: evaluation.name || undefined,
      type: `${evaluation.input_type || 'text'}|${evaluation.output_type || 'text'}`,
      prompt: evaluation.prompt || '',
      sample_count: evaluation.sample_count || 10,
    },
  });

  const currentPrompt = watch('prompt');
  const currentType = watch('type');
  const currentProviderId = watch('provider_id');
  const currentDeveloperId = watch('developer_id');
  const currentDeveloper = developers.find((d) => d.id === currentDeveloperId);

  useEffect(() => {
    const selectedModelId = getValues('model_id');
    const isValidModel = models.map((m) => m.id).includes(selectedModelId);
    if (!isValidModel) {
      setValue('model_id', '');
    }
  }, [models]);

  useEffect(() => {
    const [inputType, outputType] = currentType.split('|');
    setInputType(inputType || '');
    setOutputType(outputType || '');
  }, [currentType]);

  useEffect(() => {
    setProvider(providers.find((p) => p.id === currentProviderId));
  }, [currentProviderId]);

  useEffect(() => {
    setDeveloper(developers.find((d) => d.id === currentDeveloperId));
  }, [currentDeveloperId]);

  useEffect(() => {
    setPrompt(currentPrompt);
  }, [currentPrompt]);

  const onRunSampleClick = (data: any) => {
    const issues = getErrorMessages({
      extractedFields,
      targetColumn: data.target_column,
      type: data.type,
      schema,
    });
    setErrorMessages(issues);

    if (issues.length > 0) {
      return;
    }
    // HACK: add the model to the evaluation data to keep the run without sample working
    const evaluationData = {
      ...data,
      model: models.find((m) => m.id === data.model_id),
      input_type: inputType,
      output_type: outputType,
    };
    onRunSample(evaluationData as IEvaluation);
  };

  const onNextClick = (data: any) => {
    const issues = getErrorMessages({
      extractedFields,
      targetColumn: data.target_column,
      type: data.type,
      schema,
    });
    setErrorMessages(issues);

    if (issues.length > 0) {
      return;
    }

    // HACK: add the model to the evaluation data to keep the run without sample working
    const evaluationData = {
      ...data,
      model: models.find((m) => m.id === data.model_id),
      input_type: inputType,
      output_type: outputType,
    };
    onNext(evaluationData as IEvaluation);
  };

  return (
    <Styled.SetupForm {...rest}>
      <Styled.SplitFormWrapper>
        <Stack gap={20} data-tutorialid="evaluation-wizard-setup-config">
          <SourceFileInfo evaluation={evaluation} />
          <Input.Select
            name="type"
            label="Evaluation Type"
            error={errors?.type}
            control={control}
            options={typeOptions}
            wide
          />

          <DeveloperSelect
            name="developer_id"
            label="Developer"
            options={developers as TModelDeveloper[]}
            control={control}
            defaultIcon={currentDeveloper ? {
              logoUrl: currentDeveloper?.logo,
              displayName: currentDeveloper?.display_name,
              name: currentDeveloper?.name,
            } : undefined}
            error={errors?.developer_id}
          />

          {/* <ProviderSelect
            name="provider_id"
            label="Provider"
            options={providers}
            control={control}
            defaultIcon={currentProvider ? {
              logoUrl: currentProvider?.logo,
              displayName: currentProvider?.display_name,
              name: currentProvider?.name,
            } : undefined}
            error={errors?.provider_id}
          /> */}

          <ModelSelect
            name="model_id"
            label="Model"
            options={models}
            control={control}
            defaultIcon={currentDeveloper ? {
              logoUrl: currentDeveloper?.logo,
              displayName: currentDeveloper?.display_name,
              name: currentDeveloper?.name,
            } : undefined}
            error={errors?.model_id}
          />

          <TargetFieldSelect
            label="Output Column"
            name="target_column"
            error={errors?.target_column}
            schema={schema}
            control={control}
            wide
          />
        </Stack>

        <Stack gap={20} data-tutorialid="evaluation-wizard-setup-prompt">
          <Input name="name" label="Evaluation Name" submit={register} error={errors?.name} wide />
          {outputType === 'embeddings' ? (
            <Input.Select
              name="prompt"
              label="Embedding Field"
              options={schema.fields.map((field) => ({ value: field.name, label: field.name }))}
              control={control}
              error={errors?.prompt}
              hint="The field to generate embeddings for"
            />
          ) : (
            <PromptEditorControl
              schema={schema}
              label="Prompt"
              hint="Use curly braces {FIELD_NAME} to specify fields to interpret in the request."
              name="prompt"
              minLines={4}
              placeholder={promptPlaceholder}
              control={control}
              error={errors?.prompt}
            />
          )}
          <Row alignItems="flex-end" justifyContent="space-between">
            <Input.Select
              name="sample_count"
              label="Sample size"
              control={control}
              options={[1, 5, 10, 20, 25].map((count) => ({ value: count, label: pluralize('row', count, true) }))}
            />
            <Row alignItems="center" gap={10}>
              <Button.Primary
                small
                onClick={handleSubmit(onRunSampleClick)}
                disabled={['started', 'queued'].includes(evaluation?.status)}
              >
                {evaluation?.status === 'completed' ? 'Run again' : 'Run sample'}
              </Button.Primary>
              <Button.Basic small onClick={handleSubmit(onNextClick)}>
                Next <Icon.ArrowRight />
              </Button.Basic>
            </Row>
          </Row>
        </Stack>
        <SchemaRail
          schema={schema as IDataFrameSchema}
          extractedFields={extractedFields}
          data-tutorialid="evaluation-wizard-schema-rail"
        />
        {children}
      </Styled.SplitFormWrapper>
      {errorMessages.length > 0 && (
        <Card.Error>
          {errorMessages.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </Card.Error>
      )}
      {/* <DevTool control={control} /> */}
    </Styled.SetupForm>
  );
};

export default SetupForm;
